import {useMemo, useState} from "react";
import {buildScreens} from "../../../../../utils/buildScreens";

/**
 * Only interactive blocks gate navigation.
 * Question blocks never block the learner, so a wrong answer can't get them stuck.
 */
const isRequiredBlock = (block) => block.type === "interactive";

/**
 * Manages lesson screens, per-screen block completion, navigation and progress.
 * State resets automatically when the component using it remounts (see `key` in CourseContent).
 */
const useLessonProgress = (fragments) => {
    const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
    const [completedBlocks, setCompletedBlocks] = useState({});

    // Becomes true when the learner finishes the last screen
    const [isFinished, setIsFinished] = useState(false);

    const screens = useMemo(
        () => (fragments ? buildScreens(fragments) : []),
        [fragments]
    );

    const currentScreen = screens[currentScreenIndex];
    const isLastScreen = currentScreenIndex === screens.length - 1;

    // Memoized so the empty-Set fallback doesn't change identity on every render
    const currentCompletedBlocks = useMemo(
        () => completedBlocks[currentScreenIndex] || new Set(),
        [completedBlocks, currentScreenIndex]
    );

    // A screen is complete when all its required blocks are done
    // (screens without required blocks are complete immediately)
    const isScreenComplete = useMemo(() => {
        if (!currentScreen) return false;

        return currentScreen.blocks
            .filter(isRequiredBlock)
            .every((block) => currentCompletedBlocks.has(block.id));
    }, [currentScreen, currentCompletedBlocks]);

    const handleBlockComplete = (blockId) => {
        setCompletedBlocks((prev) => ({
            ...prev,
            [currentScreenIndex]: new Set([
                ...(prev[currentScreenIndex] || []),
                blockId,
            ]),
        }));
    };

    const goNext = () => {
        if (!isScreenComplete) return;

        // Last screen: finish the lesson instead of moving to another screen
        if (isLastScreen) {
            setIsFinished(true);
            return;
        }

        setCurrentScreenIndex((prev) => prev + 1);
    };

    const goPrevious = () => {
        if (currentScreenIndex <= 0) return;

        setCurrentScreenIndex((prev) => prev - 1);
    };

    const progress = isFinished
        ? 100
        : screens.length
            ? ((currentScreenIndex + 1) / screens.length) * 100
            : 0;

    return {
        currentScreen,
        currentCompletedBlocks,
        currentScreenIndex,
        isScreenComplete,
        isLastScreen,
        isFinished,
        progress,
        handleBlockComplete,
        goNext,
        goPrevious,
    };
};

export default useLessonProgress;